import { formatRecoveryCode, passwordProblems, type AccountView, type MfaSetup, type Session } from '@cyberscore/contracts'; // prettier-ignore
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, LogOut, Monitor, ShieldCheck, Smartphone, UserRound } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { issueFor } from '../../lib/auth';
import { useSession } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { PasswordRules } from './AuthPages';
import '../business/business.css';
import './auth.css';

const KEY = ['auth', 'account'];

function useAccount() {
  const { me } = useSession();
  return useQuery({
    queryKey: KEY,
    queryFn: () => api<AccountView>('GET', '/auth/account'),
    enabled: !!me?.session.own,
  });
}

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/** A rough device name from the browser's user agent. */
const device = (ua: string | null) => {
  if (!ua) return 'Unknown device';
  const os = /iPhone|iPad/.test(ua) ? 'iPhone or iPad' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'Device'; // prettier-ignore
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser'; // prettier-ignore
  return `${browser} on ${os}`;
};

/**
 * Your account (decision 0033): name, password, two-step sign-in and where you are signed in. Accounts
 * from an outside identity provider are managed there.
 */
export function AccountPage() {
  const { me } = useSession();
  const account = useAccount();
  if (!me?.session.own)
    return (
      <div className="biz-plain">
        <section className="bcard">
          <h2>Your account</h2>
          <p className="muted mt-s">
            Your organisation manages your sign-in. Change your password or two-step sign-in there.
          </p>{' '}
          {/* prettier-ignore */}
        </section>
      </div>
    );
  if (account.isPending) return <div className="empty">Loading…</div>;
  if (account.isError) return <div className="empty">{account.error.message}</div>;
  const a = account.data;
  return (
    <div className="account-grid">
      <div className="bpage-head">
        <div>
          <h1>Your account</h1>
          <p>{a.email}</p>
        </div>
      </div>
      <Profile a={a} />
      <TwoStep a={a} />
      <ChangePassword />
      <Sessions a={a} />
    </div>
  );
}

function Card({
  icon,
  title,
  sub,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  // prettier-ignore
  return (
    <section className="bcard">
      <div className="bcard-head">
        <span className="hicon">{icon}</span>
        <div>
          <h3>{title}</h3>
          {sub && <div className="sub">{sub}</div>}
        </div>
      </div>
      <div className="mt">{children}</div>
    </section>
  );
}

function Profile({ a }: { a: AccountView }) {
  const qc = useQueryClient();
  const { reload } = useSession();
  const toast = useToast();
  const [name, setName] = useState(a.name);
  const save = useMutation({
    mutationFn: () => api<AccountView>('PATCH', '/auth/account', { name }),
    onSuccess: (v) => {
      qc.setQueryData(KEY, v);
      void reload();
      toast('Name saved');
    },
    onError: (e) => toast(e.message, 'bad'),
  });
  return (
    <Card icon={<UserRound size={18} />} title="Profile">
      <form className="auth-form" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="auth-field">
          <label htmlFor="acct-name">Name</label>
          <input id="acct-name" className="auth-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </div>
        <div className="auth-field">
          <label htmlFor="acct-email">Email</label>
          <input id="acct-email" className="auth-input" value={a.email} disabled />
        </div>
        <div><button type="submit" className="bbtn" disabled={save.isPending || !name.trim() || name.trim() === a.name}>Save</button></div>
      </form>
    </Card>
  ); // prettier-ignore
}

function ChangePassword() {
  const { signedIn } = useSession();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const change = useMutation({
    mutationFn: () => api<Session>('POST', '/auth/password/change', { current, password }),
    onSuccess: (s) => {
      signedIn(s);
      setCurrent('');
      setPassword('');
      toast('Password changed. Other devices were signed out.');
    },
  });
  return (
    <Card icon={<KeyRound size={18} />} title="Password" sub="Changing it signs you out on every other device.">
      <form className="auth-form" onSubmit={(e) => { e.preventDefault(); change.mutate(); }} noValidate>
        {change.isError && !issueFor(change.error, 'current') && !issueFor(change.error, 'password') && <div className="auth-err">{change.error.message}</div>}
        <div className="auth-field">
          <label htmlFor="pw-current">Current password</label>
          <input id="pw-current" className="auth-input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
          {issueFor(change.error, 'current') && <div className="auth-err">{issueFor(change.error, 'current')}</div>}
        </div>
        <div className="auth-field">
          <label htmlFor="pw-new">New password</label>
          <input id="pw-new" className="auth-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          <PasswordRules password={password} />
          {issueFor(change.error, 'password') && <div className="auth-err">{issueFor(change.error, 'password')}</div>}
        </div>
        <div><button type="submit" className="bbtn" disabled={change.isPending || !current || passwordProblems(password).length > 0}>Change password</button></div>
      </form>
    </Card>
  ); // prettier-ignore
}

function TwoStep({ a }: { a: AccountView }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [setting, setSetting] = useState(false);
  const [off, setOff] = useState({ open: false, password: '', code: '' });
  const disable = useMutation({
    mutationFn: () => api<null>('POST', '/auth/mfa/disable', { password: off.password, code: off.code }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      setOff({ open: false, password: '', code: '' });
      toast('Two-step sign-in is off');
    },
  });
  return (
    <Card icon={<Smartphone size={18} />} title="Two-step sign-in" sub="A code from an authenticator app as well as your password. Required for insurer and broker staff.">
      {a.mfa_enabled ? (
        <>
          <p><span className="badge ok">On</span> <span className="muted small">{a.recovery_codes_left} recovery codes left</span></p>
          {!off.open ? (
            <button type="button" className="bbtn sm mt" onClick={() => setOff({ ...off, open: true })}>Turn off</button>
          ) : (
            <form className="auth-form mt" onSubmit={(e) => { e.preventDefault(); disable.mutate(); }}>
              {disable.isError && <div className="auth-err">{disable.error.message}</div>}
              <div className="auth-field"><label htmlFor="off-pw">Password</label><input id="off-pw" className="auth-input" type="password" value={off.password} onChange={(e) => setOff({ ...off, password: e.target.value })} autoComplete="current-password" /></div>
              <div className="auth-field"><label htmlFor="off-code">Code from the app</label><input id="off-code" className="auth-input code" value={off.code} onChange={(e) => setOff({ ...off, code: e.target.value.replace(/\D/g, '').slice(0, 6) })} inputMode="numeric" autoComplete="one-time-code" /></div>
              <div className="row gap-s"><button type="submit" className="bbtn" disabled={disable.isPending || !off.password || off.code.length < 6}>Turn off two-step sign-in</button><button type="button" className="bbtn" onClick={() => setOff({ open: false, password: '', code: '' })}>Cancel</button></div>
            </form>
          )}
        </>
      ) : setting ? (
        <TwoStepSetup onDone={() => { setSetting(false); void qc.invalidateQueries({ queryKey: KEY }); }} />
      ) : (
        <>
          <p className="muted">Off. Turn it on so a stolen password alone can’t open your account.</p>
          <button type="button" className="bbtn pri mt" onClick={() => setSetting(true)}>Turn on two-step sign-in</button>
        </>
      )}
    </Card>
  ); // prettier-ignore
}

/** Scan, enter a code, save the recovery codes. Used on the account page and by the staff gate. */
export function TwoStepSetup({ onDone }: { onDone: () => void }) {
  const { signedIn } = useSession();
  const [svg, setSvg] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState<(Session & { recovery_codes: string[] }) | null>(null);
  const codes = done?.recovery_codes ?? null;
  const setup = useQuery({
    queryKey: ['auth', 'mfa-setup'],
    queryFn: () => api<MfaSetup>('POST', '/auth/mfa/setup'),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  useEffect(() => {
    if (setup.data) void QRCode.toString(setup.data.uri, { type: 'svg', margin: 0, errorCorrectionLevel: 'M' }).then(setSvg); // prettier-ignore
  }, [setup.data]);
  const enable = useMutation({
    mutationFn: () => api<Session & { recovery_codes: string[] }>('POST', '/auth/mfa/enable', { code }),
    // The new two-step session is taken up once the codes are saved: taking it up reloads the app.
    onSuccess: setDone,
  });

  if (codes)
    return (
      <div className="setup-steps">
        <p><b>Two-step sign-in is on.</b> Save these recovery codes somewhere safe, like a password manager. Each one works once, if you lose your phone.</p> {/* prettier-ignore */}
        <div className="codes" aria-label="Recovery codes">
          {codes.map((c) => <span key={c}>{formatRecoveryCode(c)}</span>)}
        </div>
        <div className="row gap-s">
          <button type="button" className="bbtn" onClick={() => void navigator.clipboard?.writeText(codes.map(formatRecoveryCode).join('\n'))}>Copy codes</button>
          <button type="button" className="bbtn pri" onClick={() => { signedIn({ access_token: done!.access_token, expires_in: done!.expires_in }); onDone(); }}>I’ve saved them</button>
        </div>
      </div>
    ); // prettier-ignore
  if (setup.isPending) return <p className="muted">Preparing…</p>;
  if (setup.isError) return <p className="auth-err">{setup.error.message}</p>;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    enable.mutate();
  };
  return (
    <ol className="setup-steps">
      <li>
        <b>1. Scan this with an authenticator app</b>
        <span className="muted small">Microsoft Authenticator, Google Authenticator, 1Password or similar.</span>
        {svg && <div className="qr" role="img" aria-label="QR code for your authenticator app" dangerouslySetInnerHTML={{ __html: svg }} />}
        <span className="muted small">Can’t scan? Enter this key in the app:</span>
        <div className="secret" data-testid="mfa-secret">{setup.data.secret}</div>
      </li>
      <li>
        <b>2. Enter the 6-digit code it shows</b>
        <form className="auth-form" onSubmit={submit}>
          {enable.isError && <div className="auth-err">{enable.error.message}</div>}
          <input className="auth-input code" aria-label="Code from the app" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000" />
          <div><button type="submit" className="bbtn pri" disabled={enable.isPending || code.length < 6}>Turn on</button></div>
        </form>
      </li>
    </ol>
  ); // prettier-ignore
}

function Sessions({ a }: { a: AccountView }) {
  const qc = useQueryClient();
  const toast = useToast();
  const end = useMutation({
    mutationFn: (id: string) => api<null>('DELETE', `/auth/sessions/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast('Signed out on that device');
    },
    onError: (e) => toast(e.message, 'bad'),
  });
  return (
    <Card icon={<Monitor size={18} />} title="Where you’re signed in" sub="Sign out anywhere you don’t recognise, then change your password.">
      {a.sessions.map((s) => (
        <div key={s.id} className="session-row">
          <span className="hicon"><Monitor size={16} /></span>
          <div className="grow">
            <b>{device(s.user_agent)}</b>
            {s.current && <span className="badge ok" style={{ marginLeft: 8 }}>This device</span>}
            <div className="muted small">Signed in {when(s.created_at)} · last active {when(s.last_used_at)}</div>
          </div>
          {!s.current && (
            <button type="button" className="bbtn sm" disabled={end.isPending} onClick={() => end.mutate(s.id)}>
              <LogOut size={14} /> Sign out
            </button>
          )}
        </div>
      ))}
    </Card>
  ); // prettier-ignore
}

/** Staff of an insurer, broker or the platform: two-step sign-in first, then the app. */
export function MfaGate() {
  const { me, signOut } = useSession();
  return (
    <div className="biz gate">
      <section className="bcard">
        <div className="bcard-head">
          <span className="hicon"><ShieldCheck size={18} /></span>
          <div>
            <h3>Turn on two-step sign-in</h3>
            <div className="sub">Your organisation works with businesses’ security data, so its staff sign in with a code from an app as well as a password.</div>
          </div>
        </div>
        <div className="mt">
          <TwoStepSetup onDone={() => {}} />
        </div>
        <p className="muted small mt">
          Signed in as {me?.user.email}. <button type="button" className="link-btn" onClick={signOut}>Sign out</button>
        </p>
      </section>
    </div>
  ); // prettier-ignore
}
